import { useEffect, useState } from "react";
import { ShippingInfo } from "../../Interfaces/ShippingInfo";
import { ContactInfo } from "./OrgShippingInfoForm";
import { useNavigate } from "react-router/dist";
import { useCart } from "../../contexts/Cart/CartContext";
import fetchSquareLink from "./FetchSquareLink";
import { validateShippingInfo } from "./ShippingAddress";
import { usePopup } from "../Popup/PopupContext";
import createPaymentIntent from "./CreatePaymentIntent";
import { getCurrentlyLoggedInUser } from "../../CommonFunctions";
import { LoggedInAccount } from "../../Interfaces/AccountUsers";
import { FundraiserResponse } from "../../Interfaces/Fundraiser";
import PopupModel from "../Popup/PopupModel";
interface RefactoredOrderSummaryProps {
  onShippingStep: boolean; // signal to say that this is called from the checkout page
  shippingOption?: string;
  contactInfo?: ContactInfo;
  shippingInfo?: ShippingInfo;
  setShippingInfo?: (shippingInfo: ShippingInfo) => void;
  shippingCost?: number;
  isValidContactInfo?: boolean;
  fundraiser?: FundraiserResponse;
}

const RefactoredOrderSummary: React.FC<RefactoredOrderSummaryProps> = ({
  onShippingStep = false,
  shippingOption = "",
  contactInfo,
  shippingInfo,
  setShippingInfo,
  shippingCost,
  isValidContactInfo,
  fundraiser,
}) => {
  const { state, nonDonationItemTotal, totalItems, subtotal, dispatch } =
    useCart();
  const { cart } = state;
  let validShipping = false;
  const { showPopup, hidePopup } = usePopup();
  const [submissionError, setSubmissionError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<LoggedInAccount | null>(null);
  const [disallowedItems, setDisallowedItems] = useState<any>("");
  const cartErrorPopup = (errorItems: string[], hidePopup: () => void) => {
    return new PopupModel(
      "Cart - Error",
      <div>
        <strong>Out of stock</strong>
        <p>The following items are no longer available.</p>
        <p>Please remove them before submitting again</p>
        <ul>
          {errorItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>,
      [
        {
          label: "Okay",
          onClick: () => {
            hidePopup();
          },
        },
      ],
      "40vw",
    );
  };

  const shipToOrgAddress: ShippingInfo = {
    city: fundraiser?.org_shipping_info?.city || "",
    street_address: fundraiser?.org_shipping_info?.street_address || "",
    zip_code: fundraiser?.org_shipping_info?.zip_code || "",
    state: fundraiser?.org_shipping_info?.state || "",
    ship_to_name: fundraiser?.org_shipping_info?.ship_to_name || "",
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const loggedInUser = await getCurrentlyLoggedInUser();
        setUser(loggedInUser);
      } catch (error) {}
    };

    fetchUser();
  }, []);

  const navigate = useNavigate();

  const beginCheckout = async () => {
    setIsLoading(true);
    validShipping = false; //reset shipping to false on new click in case they change the form
    if (totalItems === 0 || !cart || subtotal <= 0) {
      console.warn(
        "Cannot proceed to checkout: Cart is empty or subtotal is invalid.",
      );
      setIsLoading(false);
      return;
    }
    if (!onShippingStep) {
      setIsLoading(false);
      navigate("/checkout");
      return;
    }
    setSubmissionError("");
    if (nonDonationItemTotal === 0) {
      validShipping = true;
    }

    if (!isValidContactInfo) {
      setSubmissionError(
        "Invalid Contact Information. Please provide first and last name as well as an email",
      );
      setIsLoading(false);
      return;
    }
    if (nonDonationItemTotal > 0 && setShippingInfo) {
      validShipping = await validateShippingInfo(
        shippingInfo || shipToOrgAddress,
        setShippingInfo,
        showPopup,
        hidePopup,
      );
    }

    if (nonDonationItemTotal > 0 && !validShipping) {
      setSubmissionError(
        "Invalid shipping information. Please check all fields",
      );
      setIsLoading(false);
      return;
    }

    if (
      !shippingInfo?.ship_to_name ||
      !shippingInfo?.street_address ||
      !shippingInfo?.city ||
      !shippingInfo?.state ||
      !shippingInfo?.zip_code
    ) {
      // If its all dono then we dont need a shipping address
      if (nonDonationItemTotal > 0) {
        setIsLoading(false);
        return;
      }
    }
    if (contactInfo && shippingCost !== undefined) {
      if (isValidContactInfo && validShipping) {
        const response = await fetchSquareLink(
          contactInfo,
          cart,
          subtotal,
          shippingInfo || shipToOrgAddress,
          nonDonationItemTotal > 0 ? shippingCost : 0,
        );

        if (
          response &&
          "error" in response &&
          response.error === "disallowed_items"
        ) {
          const cartError = cartErrorPopup(response.disallowedItems, hidePopup);
          showPopup(
            cartError.header,
            cartError.body,
            cartError.buttons,
            cartError.width,
          );
        } else if (
          response &&
          "status" in response &&
          response?.status === 200
        ) {
          const paymentIntentResponse = await createPaymentIntent(
            shippingInfo || shipToOrgAddress,
            shippingOption,
            contactInfo?.full_name || "",
            contactInfo?.email || "",
            contactInfo?.phone || "",
            cart,
            state.fundraiser_participant?.id || 0,
            user,
            response.data.orderID,
            shippingCost.toString(),
          );
          // before navigating to the page, save the necessary info for checkout success
          localStorage.setItem("shippingInfo", JSON.stringify(shippingInfo));
          localStorage.setItem("shippingOption", shippingOption);
          localStorage.setItem("squareOrderID", response?.data.orderID);
          localStorage.setItem("customerName", contactInfo?.full_name || "");
          localStorage.setItem("customerEmail", contactInfo?.email || "");
          localStorage.setItem("customerPhoneNumber", contactInfo?.phone || "");
          localStorage.setItem("shippingCost", shippingCost.toString());

          if (paymentIntentResponse?.status === 200) {
            setIsLoading(false);
            window.location.href = response.data.url;
          }
        }
      } else {
        console.log(
          "Need to put some error here. But basically we told the component we were on shipping but didnt provide it all the shipping details",
        );
      }
    }
    // this is mostly for testing -> my best guess is that shippo has a regulator on test shipping calls
    // which makes us unable to attempt the resubmission
    setIsLoading(false);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Order Summary</h2>
      <div style={styles.field}>
        <span>Item Subtotal:</span>
        <span>${subtotal.toFixed(2)}</span>
      </div>
      <hr style={styles.separator} />
      <button
        onClick={beginCheckout}
        style={
          isLoading ? styles.checkoutButtonDisabled : styles.checkoutButton
        }
        disabled={isLoading}
        onMouseEnter={(e) => {
          if (!isLoading) {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor =
              "#333";
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor =
              "black";
          }
        }}
      >
        {isLoading ? "Loading..." : "Checkout"}
      </button>

      {submissionError && <p style={styles.errorText}>{submissionError}</p>}
    </div>
  );
};

export default RefactoredOrderSummary;

const styles: Record<string, React.CSSProperties> = {
  container: {
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "20px",
    width: "100%",
    fontFamily: "Arial, sans-serif",
    maxWidth: "450px",
    boxSizing: "border-box",
    margin: "0 auto", // centers on desktop
  },
  header: {
    margin: "0 0 20px 0",
    fontSize: "1.25rem",
    fontWeight: 600,
    textAlign: "center",
  },
  field: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px",
    flexWrap: "wrap" as "wrap",
    fontSize: "0.95rem",
  },
  separator: {
    margin: "15px 0",
    border: "0",
    borderTop: "1px solid #e0e0e0",
  },
  checkoutButton: {
    backgroundColor: "black",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "12px 20px",
    cursor: "pointer",
    width: "100%",
    fontSize: "1rem",
    transition: "background-color 0.2s ease",
  },
  checkoutButtonHover: {
    backgroundColor: "#333",
  },
  checkoutButtonDisabled: {
    backgroundColor: "#d3d3d3",
    color: "#808080",
    border: "none",
    borderRadius: "6px",
    padding: "12px 20px",
    cursor: "not-allowed",
    width: "100%",
    opacity: 0.7,
    fontSize: "1rem",
  },
  errorText: {
    color: "#d9534f",
    fontSize: "0.9rem",
    marginTop: "10px",
    textAlign: "center",
    wordBreak: "break-word", // prevents long messages from overflowing
  },
};
